import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaCalendarAlt, FaClock, FaUsers, FaStar } from 'react-icons/fa';

interface Activity {
  _id?: string;
  title: string;
  description: string;
  type: 'craft' | 'game' | 'story' | 'quiz';
  instructions: string[];
  materials?: string[];
  imageUrl?: string;
}

interface HolidayPack {
  _id: string;
  title: string;
  description: string;
  holiday: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  estimatedTime: number;
  wordCount: number;
  activities: Activity[];
  culturalNotes: string;
  createdAt: string;
}

const HolidayPacks: React.FC = () => {
  const [holidayPacks, setHolidayPacks] = useState<HolidayPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('all');

  useEffect(() => {
    fetchHolidayPacks();
  }, []);

  const fetchHolidayPacks = async () => {
    try {
      const response = await fetch('/api/cultural/holidays');
      const data = await response.json();
      
      if (data.success) {
        setHolidayPacks(data.data);
      } else {
        setError(data.message || 'Failed to fetch holiday packs');
      }
    } catch (err) {
      setError('Failed to fetch holiday packs');
      console.error('Error fetching holiday packs:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredPacks = holidayPacks.filter(pack => 
    selectedDifficulty === 'all' || pack.difficulty === selectedDifficulty
  );

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'intermediate': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'advanced': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getDifficultyText = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'مبتدی';
      case 'intermediate': return 'متوسط';
      case 'advanced': return 'پیشرفته';
      default: return difficulty;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 text-xl mb-4">⚠️</div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">خطا در بارگذاری</h2>
        <p className="text-gray-600 dark:text-gray-400">{error}</p>
        <button 
          onClick={fetchHolidayPacks}
          className="mt-4 px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          تلاش مجدد
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold dark:text-white mb-4">
          بسته‌های تعطیلات 🎉
        </h1>
        <p className="text-lg dark:text-gray-300">
          یادگیری فرهنگ و زبان فارسی از طریق جشن‌ها و مناسبت‌های ایرانی
        </p>
      </div>

      {/* Filter */}
      <div className="flex justify-center">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-1 shadow-lg">
          <button
            onClick={() => setSelectedDifficulty('all')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              selectedDifficulty === 'all'
                ? 'bg-blue-500 text-white'
                : 'text-gray-700 dark:text-gray-300 hover:text-blue-500 dark:hover:text-blue-400'
            }`}
          >
            همه
          </button>
          <button
            onClick={() => setSelectedDifficulty('beginner')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              selectedDifficulty === 'beginner'
                ? 'bg-blue-500 text-white'
                : 'text-gray-700 dark:text-gray-300 hover:text-blue-500 dark:hover:text-blue-400'
            }`}
          >
            مبتدی
          </button>
          <button
            onClick={() => setSelectedDifficulty('intermediate')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              selectedDifficulty === 'intermediate'
                ? 'bg-blue-500 text-white'
                : 'text-gray-700 dark:text-gray-300 hover:text-blue-500 dark:hover:text-blue-400'
            }`}
          >
            متوسط
          </button>
          <button
            onClick={() => setSelectedDifficulty('advanced')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              selectedDifficulty === 'advanced'
                ? 'bg-blue-500 text-white'
                : 'text-gray-700 dark:text-gray-300 hover:text-blue-500 dark:hover:text-blue-400'
            }`}
          >
            پیشرفته
          </button>
        </div>
      </div>

      {/* Holiday Packs Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredPacks.map((pack) => (
          <div key={pack._id} className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getDifficultyColor(pack.difficulty)}`}>
                  {getDifficultyText(pack.difficulty)}
                </span>
                <FaCalendarAlt className="text-blue-500" />
              </div>
              
              <h3 className="text-xl font-bold dark:text-white mb-2">
                {pack.title}
              </h3>
              
              <p className="dark:text-gray-400 text-sm mb-4 line-clamp-3">
                {pack.description}
              </p>
              
              <div className="space-y-2 mb-4">
                <div className="flex items-center text-sm dark:text-gray-400">
                  <FaClock className="mr-2" />
                  <span>{pack.estimatedTime} دقیقه</span>
                </div>
                <div className="flex items-center text-sm dark:text-gray-400">
                  <FaUsers className="mr-2" />
                  <span>{pack.wordCount} کلمه</span>
                </div>
              </div>
              
              <div className="mb-4">
                <h4 className="font-semibold dark:text-white mb-2">فعالیت‌ها:</h4>
                <div className="flex flex-wrap gap-1">
                  {(pack.activities || []).slice(0, 3).map((activity, index) => (
                    <span key={activity._id || index} className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded text-xs">
                      {activity.title}
                    </span>
                  ))}
                  {(pack.activities || []).length > 3 && (
                    <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 dark:text-gray-400 rounded text-xs">
                      +{(pack.activities || []).length - 3} بیشتر
                    </span>
                  )}
                </div>
              </div>
              
              <Link
                to={`/cultural/holidays/${pack._id}`}
                className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-semibold py-3 px-6 rounded-lg hover:from-blue-600 hover:to-indigo-600 transition-all duration-300 text-center block"
              >
                شروع یادگیری
              </Link>
            </div>
          </div>
        ))}
      </div>

      {filteredPacks.length === 0 && (
        <div className="text-center py-12">
          <div className="dark:text-gray-400 text-4xl mb-4">📅</div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            بسته‌ای یافت نشد
          </h3>
          <p className="dark:text-gray-400">
            هیچ بسته تعطیلاتی برای سطح انتخاب شده وجود ندارد.
          </p>
        </div>
      )}
    </div>
  );
};

export default HolidayPacks; 